import {
  Plugin,
  PluginManager as IPluginManager,
  PluginType,
} from '@accedo/one-plugin-sdk';
import React, { ComponentType, PropsWithChildren } from 'react';
import styled from 'styled-components';

import devtoolsApi from '#/dev/devtools';
import { RawFeaturesConfig } from '#/service/config';

import { loadScript } from './utils/scripts';
import withConfig from './withConfig';

interface PluginToLoad {
  type?: PluginType;
  id?: string;
  src?: string;
  config: object;
}

declare global {
  // TS doesn't know about `Promise.allSettled` yet.
  interface PromiseConstructor {
    allSettled(
      promises: Array<Promise<any>>
    ): Promise<Array<{ status: string; value?: any; reason?: any }>>;
  }
}

interface GetPluginComponentParams {
  type: PluginType;
  id?: string;
  optional?: boolean;
}

// unique key for a plugin
const pluginKey = (type?: string, id?: string) => [type, id].join('--');

// registry to store loaded plugins
const loadedPlugins: { [key: string]: Plugin } = {};

// maps for plugin related objects
export const pluginPromises: { [key: string]: Promise<Plugin> } = {};
export const pluginConfigs: { [key: string]: object } = {};

class PluginManager implements IPluginManager {
  // load the given plugins
  loadPlugins(pluginsToLoad: PluginToLoad[]) {
    console.debug('PluginManager - Start loading plugins', pluginsToLoad);

    pluginsToLoad.forEach(({ type, id, src, config = {} }) => {
      const key = pluginKey(type, id);

      pluginConfigs[key] = config;

      pluginPromises[key] = loadScript({ src })
        .then(() => {
          const loadedPlugin = loadedPlugins[key];
          loadedPlugin.component.displayName = ['Plugin', type, id].join('-');

          return loadedPlugin;
        })
        .catch((e) => {
          console.warn('PluginManager - Failed to load', src, e);
          throw e;
        });
    });
  }

  // promise of all plugin loading promises have either resolved or rejected
  allPluginsLoadedPromise() {
    return Promise.allSettled(Object.values(pluginPromises));
  }

  // filtering and mapping feature configs to find plugins to load
  // TODO: We should move the plugin from features extraction away from this file
  // and simply call loadPlugins with the result
  loadFeaturePlugins(featuresConfig: RawFeaturesConfig) {
    const pluginsToLoad = Object.values(featuresConfig)
      .flat()
      .filter((feature) => feature.enabled && feature.plugin)
      .map(({ plugin, properties }) => ({
        type: plugin?.type,
        id: plugin?.id,
        src: plugin?.src,
        config: properties,
      }));

    this.loadPlugins(pluginsToLoad);
  }

  // returns a specific plugin component (withConfig, lazy)
  getPluginComponent<TProps>({
    type,
    id,
    optional = false,
  }: GetPluginComponentParams) {
    const emptyComponent = (_props: TProps) => null;

    if (!id) {
      return emptyComponent;
    }

    const key = pluginKey(type, id);

    // plugin is not found
    if (!(key in pluginPromises)) {
      // if it's optional, we return an empty component
      if (optional) {
        return emptyComponent;
      }

      // otherwise, put a rejected promise in the cache
      pluginPromises[key] = Promise.reject(
        `Unknown plugin ${JSON.stringify({ type, id })}`
      );
    }

    // configs for this plugin
    const pluginConfig = pluginConfigs[key] || {};

    // Return a lazy component that waits for the plugin promise and
    // resolves with a module default-exporting the component in the plugin
    return React.lazy<ComponentType<TProps>>(
      () =>
        pluginPromises[key].then((plugin) => {
          const component = plugin.component as ComponentType<
            PropsWithChildren<TProps>
          >;
          const componentWithConfig = withConfig(component, pluginConfig);

          return {
            default: componentWithConfig,
          };
        })
      // we don't catch errors here. we don't have anything to fallback to.
      // we leave that to the error boundary.
    );
  }

  // plugins will register themselves with this method
  registerPlugin(plugin: Plugin) {
    const { id, type } = plugin;
    const key = pluginKey(type, id);

    loadedPlugins[key] = plugin;

    const pluginInfo = {
      type,
      config: pluginConfigs[key],
    };

    if (process.env.goal === 'local') {
      devtoolsApi.setPluginInfo(id, pluginInfo);
    }
    console.debug('PluginManager - Registered', id, pluginInfo);
  }
}

// Create a singleton instance
const pluginManager = new PluginManager();
export default pluginManager;

// Make the registerPlugin method accessible for plugins
window.pluginManager = {
  registerPlugin: pluginManager.registerPlugin,
};

// Provide externals for the plugins
window.React = React;
window.Styled = { default: styled };
